import React from "react";
import { NavLink } from "react-router-dom";
import { getInitials } from "../lib/auth";
import NeuroGenLogo from "./NeuroGenLogo";

const NavIcons = {
  dashboard: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="6" height="6" rx="1"/>
      <rect x="9" y="1" width="6" height="6" rx="1"/>
      <rect x="1" y="9" width="6" height="6" rx="1"/>
      <rect x="9" y="9" width="6" height="6" rx="1"/>
    </svg>
  ),
  documents: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z"/>
      <path d="M9 1v4h4"/>
      <line x1="5" y1="8" x2="11" y2="8"/>
      <line x1="5" y1="11" x2="11" y2="11"/>
    </svg>
  ),
  upload: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 10V3"/>
      <polyline points="5 6 8 3 11 6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12v1a1 1 0 001 1h10a1 1 0 001-1v-1"/>
    </svg>
  ),
  batch: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 10V3"/>
      <polyline points="5 6 8 3 11 6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12v1a1 1 0 001 1h10a1 1 0 001-1v-1"/>
      <line x1="4" y1="9" x2="2" y2="9"/>
      <line x1="14" y1="9" x2="12" y2="9"/>
    </svg>
  ),
  agent: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14 8c0 3.314-2.686 6-6 6a5.99 5.99 0 01-3-0.8L1 14l0.8-4A6 6 0 1114 8z"/>
      <circle cx="5.5" cy="8" r="0.75" fill="currentColor" stroke="none"/>
      <circle cx="8"   cy="8" r="0.75" fill="currentColor" stroke="none"/>
      <circle cx="10.5" cy="8" r="0.75" fill="currentColor" stroke="none"/>
    </svg>
  ),
  guide: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="6"/>
      <path d="M6.5 6a1.5 1.5 0 013 0c0 1.5-1.5 1.5-1.5 3"/>
      <circle cx="8" cy="12" r=".5" fill="currentColor" stroke="none"/>
    </svg>
  ),
  audit: (
    <svg className="nav-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M13 2H3a1 1 0 00-1 1v10a1 1 0 001 1h10a1 1 0 001-1V3a1 1 0 00-1-1z"/>
      <line x1="5" y1="5" x2="11" y2="5"/>
      <line x1="5" y1="8" x2="11" y2="8"/>
      <line x1="5" y1="11" x2="8"  y2="11"/>
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"
      style={{ width: 14, height: 14, color: "var(--text-on-dark-dim)" }}>
      <path d="M10 2h3a1 1 0 011 1v10a1 1 0 01-1 1h-3"/>
      <polyline points="7 11 10 8 7 5" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="10" y1="8" x2="2" y2="8"/>
    </svg>
  ),
};

export default function Sidebar({ user, onLogout, failedCount = 0, userRoles = [] }) {
  const initials     = getInitials(user?.name || user?.username || "QA");
  const displayName  = user?.name || user?.username || "QA Reviewer";

  const navLink = (to, icon, label, badge = null) => (
    <NavLink to={to} className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>
      {icon} {label}
      {badge}
    </NavLink>
  );

  return (
    <aside className="sidebar">

      {/* Brand */}
      <div className="sidebar-brand">
        <NeuroGenLogo size={38} variant="light" subtitle="QA PORTAL" />
      </div>

      {/* Nav */}
      <nav className="sidebar-nav">

        <span className="nav-group-label">Overview</span>
        {navLink("/dashboard", NavIcons.dashboard, "Dashboard")}

        <span className="nav-group-label">Cases</span>
        {navLink(
          "/documents",
          NavIcons.documents,
          "All Cases",
          failedCount > 0 && <span className="nav-badge">{failedCount}</span>
        )}
        {navLink("/upload", NavIcons.upload, "Upload")}

        <span className="nav-group-label">Intelligence</span>
        {navLink("/agent", NavIcons.agent, "AI Assistant")}

        <span className="nav-group-label">Reporting</span>
        {navLink("/audit", NavIcons.audit, "Audit Reports")}

        <span className="nav-group-label">Help</span>
        {navLink("/guide", NavIcons.guide, "User Guide")}

        {/* â”€â”€ Admin section (visible only to Admin role) â”€â”€ */}
        {userRoles.includes("Admin") && (
          <>
            <span className="nav-group-label">Admin</span>
            {navLink("/settings/qa-checks",
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M12 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V3a1 1 0 00-1-1z"/>
                <path d="M6 5h4M6 8h4M6 11h2"/>
              </svg>,
              "QA Checks"
            )}
          </>
        )}

      </nav>

      {/* User */}
      <div className="sidebar-user">
        <div className="user-tile" onClick={onLogout} title="Sign out">
          <div className="user-avatar">{initials}</div>
          <div className="user-details">
            <div className="user-name">{displayName}</div>
            <div className="user-role">{userRoles.includes("Admin") ? "Administrator" : "QA Reviewer"}</div>
          </div>
          {NavIcons.logout}
        </div>
      </div>

    </aside>
  );
}


